import React, { useRef, useState } from "react";
import { ChevronLeft, Copy, Volume2, FileDown } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import html2pdf from "html2pdf.js";
import { Summary } from "@/features/summaries/types";
import { useToast } from "@/hooks/use-toast";

interface MarkdownSummaryViewerProps {
  summary: Summary;
  onBack: () => void;
}

export const MarkdownSummaryViewer: React.FC<MarkdownSummaryViewerProps> = ({ summary, onBack }) => {
  const [copiedText, setCopiedText] = useState("");
  const { toast } = useToast();
  const contentRef = useRef<HTMLDivElement | null>(null);

  const handleCopy = () => {
    navigator.clipboard.writeText(summary.content || "");
    setCopiedText("Copied!");
    setTimeout(() => setCopiedText(""), 2000);
  };

  const handleAudio = () => {
    toast({
      title: "Coming soon",
      description: "Text-to-speech will be available soon.",
    });
  };

  const handleExportPDF = () => {
    const element = contentRef.current;
    if (!element) return;

    html2pdf()
      .from(element)
      .set({
        margin: 10,
        filename: `${summary.title || "summary"}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      })
      .save();
  };

  return (
    <div className="flex h-full flex-col bg-background">
      {/* Back Button - Sticky */}
      <div className="sticky top-0 z-10 shrink-0 bg-background px-4 pb-2 pt-4">
        <button
          onClick={onBack}
          className="flex h-fit w-fit items-center gap-2 rounded-lg bg-transparent p-0 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      {/* Content Area */}
      <div className="relative flex-1 overflow-y-auto">
        <div className="px-4">
          <div
            ref={contentRef}
            className="prose prose-neutral dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground/90 prose-strong:text-foreground prose-li:text-foreground/90"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 className="text-xl font-bold mb-4 text-foreground">{children}</h1>,
                h2: ({ children }) => <h2 className="text-lg font-semibold mb-3 mt-6 text-foreground">{children}</h2>,
                h3: ({ children }) => <h3 className="text-base font-semibold mb-2 mt-4 text-foreground">{children}</h3>,
                p: ({ children }) => <p className="text-base leading-7 mb-4 text-foreground/90">{children}</p>,
                ul: ({ children }) => <ul className="list-disc pl-5 space-y-2 mb-4">{children}</ul>,
                ol: ({ children }) => <ol className="list-decimal pl-5 space-y-2 mb-4">{children}</ol>,
                li: ({ children }) => <li className="text-base leading-7 text-foreground/90">{children}</li>,
                strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
                blockquote: ({ children }) => (
                  <blockquote className="border-l-4 border-primary/30 pl-4 italic text-muted-foreground my-4">
                    {children}
                  </blockquote>
                ),
              }}
            >
              {summary.content || ""}
            </ReactMarkdown>
          </div>
        </div>
      </div>

      {/* Action Bar - Bottom */}
      <div className="py-3 px-4 bg-background">
        <div className="flex flex-row items-center justify-between space-x-1">
          <div className="flex flex-row items-center space-x-1">
            {/* Copy Button */}
            <button
              onClick={handleCopy}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Copy to clipboard"
            >
              <Copy className="h-4 w-4" />
            </button>

            {/* Audio Button */}
            <button
              onClick={handleAudio}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Read aloud"
            >
              <Volume2 className="h-4 w-4" />
            </button>

            {/* Download Button */}
            <button
              onClick={handleExportPDF}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors hover:bg-muted hover:text-foreground h-7 w-7 p-1.5 text-muted-foreground"
              title="Export to PDF"
            >
              <FileDown className="h-4 w-4" />
            </button>
          </div>

          {/* Copy confirmation text */}
          {copiedText && <span className="text-xs text-correct animate-in fade-in-0">{copiedText}</span>}
        </div>
      </div>
    </div>
  );
};

export default MarkdownSummaryViewer;
